import { useState, useRef, useEffect, type JSX, type CSSProperties } from 'react';
import { ChevronDown } from 'lucide-react';
import clsx from 'clsx';

export interface ArbiSelectOption<T extends string | number = string> {
  value: T;
  label: string;
}

interface ArbiSelectProps<T extends string | number = string> {
  value: T | null | undefined;
  options: ArbiSelectOption<T>[];
  onChange: (value: T) => void;
  placeholder?: string;
  className?: string;
  size?: 'sm' | 'xs';
  disabled?: boolean;
  portal?: boolean;
}

/**
 * ArbiSelect — custom dropdown replacing native <select>.
 * Matches Arbi dark/light theme. Never uses browser default UI.
 */
export default function ArbiSelect<T extends string | number = string>({
  value,
  options,
  onChange,
  placeholder = 'Select…',
  className,
  size = 'sm',
  disabled = false,
  portal = false,
}: ArbiSelectProps<T>): JSX.Element {
  const [open, setOpen] = useState<boolean>(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  });

  const selected = options.find((o) => o.value === value);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node | null;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        // For portal mode, also check if click is on the menu
        const menu = document.getElementById('arbi-select-portal');
        if (menu && menu.contains(target)) return;
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  // Calculate position for portal mode
  useEffect(() => {
    if (!open || !portal || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
  }, [open, portal]);

  const handleSelect = (val: T): void => {
    onChange(val);
    setOpen(false);
  };

  const sizeClasses = size === 'xs' ? 'text-[11px] px-2 py-1 gap-1' : 'text-xs px-3 py-1.5 gap-1.5';
  const menuSizeClasses = size === 'xs' ? 'text-[11px] py-0.5' : 'text-xs py-1';

  const trigger = (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (!disabled) setOpen((v) => !v);
      }}
      disabled={disabled}
      className={clsx(
        'flex items-center justify-between w-full rounded-md',
        'bg-surface-3 border border-border transition-colors',
        'text-left select-none',
        open && 'border-accent',
        disabled && 'opacity-50',
        sizeClasses,
        className
      )}
    >
      <span className={clsx(selected ? 'text-ink-primary' : 'text-ink-muted')}>
        {selected?.label || placeholder}
      </span>
      <ChevronDown
        size={size === 'xs' ? 10 : 12}
        className={clsx('text-ink-muted transition-transform', open && 'rotate-180')}
      />
    </button>
  );

  const menuStyle: CSSProperties = portal
    ? {
        position: 'fixed',
        top: menuPos.top,
        left: menuPos.left,
        width: menuPos.width,
        zIndex: 9999,
        maxHeight: 200,
      }
    : {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 50,
        marginTop: 2,
        maxHeight: 200,
      };

  const menu = open && (
    <div
      id={portal ? 'arbi-select-portal' : undefined}
      className={clsx(
        'bg-surface-2 border border-border rounded-lg shadow-xl overflow-y-auto',
        menuSizeClasses
      )}
      style={menuStyle}
    >
      {options.map((opt) => (
        <button
          key={String(opt.value)}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleSelect(opt.value);
          }}
          className={clsx(
            'w-full text-left px-3 py-1.5 transition-colors',
            opt.value === value ? 'bg-accent/15 text-accent' : 'text-ink-primary hover:bg-surface-3'
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );

  return (
    <div ref={wrapperRef} className="relative" style={{ position: 'relative' }}>
      {trigger}
      {!portal && menu}
      {portal && open && menu}
    </div>
  );
}
